/**
 * 🧟 Zombie Sessions Panel
 * 
 * Dashboard UX/UI profesional para detectar y gestionar sesiones zombie
 * (sesiones pausadas sin renderizar modal en frontend)
 * 
 * Features:
 * - Métricas clave con alertas visuales
 * - Filtro por agente y rango de tiempo
 * - Tabla detallada con estado de moderación/freeze
 * - Acciones bulk: Cerrar o Eliminar sesiones
 * - Vista de detalles con timeline de eventos
 */

import React, { useState, useEffect } from 'react';
import {
  AlertTriangle,
  XCircle,
  Clock,
  Trash2,
  CheckCircle,
  RefreshCw,
  Filter,
  Download,
  Eye,
  AlertOctagon,
  Activity,
  Zap,
  X,
  ShieldAlert
} from 'lucide-react';
import axios from 'axios';
import { getAnalyticsV4Url } from '../config/config';

const ZombieSessionsPanel = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Métricas
  const [metrics, setMetrics] = useState(null);
  
  // Sesiones
  const [sessions, setSessions] = useState([]);
  const [selectedSessions, setSelectedSessions] = useState([]);
  
  // Filtros
  const [filters, setFilters] = useState({
    agentId: 'all',
    rangeDays: 7,
    minMinutes: 5,
    page: 1,
    limit: 20
  });
  
  // Agentes disponibles
  const [agents, setAgents] = useState([]);
  
  // Análisis adicional
  const [topAgents, setTopAgents] = useState([]);
  const [oldestZombies, setOldestZombies] = useState([]);
  const [dailyTrend, setDailyTrend] = useState([]);
  
  // Modal de detalles
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionDetails, setSessionDetails] = useState(null);
  
  // Confirmación de limpieza
  const [showCleanupModal, setShowCleanupModal] = useState(false);
  const [cleanupAction, setCleanupAction] = useState('close');
  
  useEffect(() => {
    fetchData();
  }, [filters.rangeDays, filters.agentId, filters.minMinutes, filters.page]);
  
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const baseURL = getAnalyticsV4Url();
      const token = localStorage.getItem('token');
      
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      // Calcular rango de fechas
      const to = new Date().toISOString();
      const from = new Date(Date.now() - filters.rangeDays * 24 * 60 * 60 * 1000).toISOString();
      
      // 1. Obtener métricas
      const metricsRes = await axios.get(`${baseURL}/analytics/zombie/metrics`, {
        params: {
          from,
          to,
          agentId: filters.agentId !== 'all' ? filters.agentId : undefined
        },
        headers
      });
      
      setMetrics(metricsRes.data.metrics);
      
      // 2. Obtener sesiones
      const sessionsRes = await axios.get(`${baseURL}/api/vertex/analytics/zombie/sessions`, {
        params: {
          from,
          to,
          agentId: filters.agentId,
          minMinutes: filters.minMinutes,
          page: filters.page,
          limit: filters.limit
        },
        headers
      });
      
      setSessions(sessionsRes.data.sessions || []);
      
      // 3. Obtener agentes (para filtro)
      const agentsRes = await axios.get(`${baseURL}/api/vertex/analytics/zombie/agents`, {
        params: { from, to },
        headers
      });
      
      setAgents(agentsRes.data.agents || []);
      
      // 4. Calcular análisis adicional
      const allSessions = sessionsRes.data.sessions || [];
      
      // Top 3 agentes con más zombies
      const agentCounts = {};
      allSessions.forEach(s => {
        agentCounts[s.agentId] = (agentCounts[s.agentId] || 0) + 1;
      });
      const topAgentsData = Object.entries(agentCounts)
        .map(([agentId, count]) => ({ agentId, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 3);
      setTopAgents(topAgentsData);
      
      // 3 zombies más antiguos
      const oldest = [...allSessions]
        .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
        .slice(0, 3);
      setOldestZombies(oldest);
      
      // Tendencia últimos 7 días
      const last7Days = {};
      for (let i = 6; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        last7Days[dateStr] = 0;
      }
      allSessions.forEach(s => {
        const dateStr = s.createdAt.split('T')[0];
        if (last7Days[dateStr] !== undefined) {
          last7Days[dateStr]++;
        }
      });
      setDailyTrend(Object.entries(last7Days).map(([date, count]) => ({ date, count })));
      
    } catch (err) {
      console.error('Error fetching zombie data:', err);
      setError(err.response?.data?.message || 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSelectSession = (sessionId) => {
    if (selectedSessions.includes(sessionId)) {
      setSelectedSessions(selectedSessions.filter(id => id !== sessionId));
    } else {
      setSelectedSessions([...selectedSessions, sessionId]);
    }
  };
  
  const handleSelectAll = () => {
    if (selectedSessions.length === sessions.length) {
      setSelectedSessions([]);
    } else {
      setSelectedSessions(sessions.map(s => s.sessionId));
    }
  };
  
  const handleViewDetails = async (sessionId) => {
    try {
      const baseURL = getAnalyticsV4Url();
      const token = localStorage.getItem('token');
      
      const res = await axios.get(
        `${baseURL}/api/vertex/analytics/zombie/session/${sessionId}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      setSessionDetails(res.data);
      setSelectedSession(sessionId);
      setShowDetailsModal(true);
    } catch (err) {
      console.error('Error fetching session details:', err);
      alert('Error al cargar detalles de sesión');
    }
  };
  
  const handleCleanup = async () => {
    if (selectedSessions.length === 0) {
      alert('Selecciona al menos una sesión');
      return;
    }
    
    setShowCleanupModal(true);
  };
  
  const confirmCleanup = async () => {
    try {
      const baseURL = getAnalyticsV4Url();
      const token = localStorage.getItem('token');
      
      const res = await axios.post(
        `${baseURL}/api/vertex/analytics/zombie/cleanup`,
        {
          sessionIds: selectedSessions,
          action: cleanupAction
        },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      alert(`✅ ${cleanupAction === 'delete' ? 'Eliminadas' : 'Cerradas'} ${res.data.modifiedCount || res.data.deletedCount} sesiones`);
      
      setSelectedSessions([]);
      setShowCleanupModal(false);
      fetchData();
    } catch (err) {
      console.error('Error cleaning up sessions:', err);
      alert('Error al limpiar sesiones');
    }
  };
  
  const formatDuration = (ms) => {
    const minutes = Math.floor(ms / 60000);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    }
    return `${minutes}m`;
  };
  
  const formatDate = (date) => {
    return new Date(date).toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Colores de alerta por criticidad
  const getAlertColor = (isCritical) => {
    return isCritical 
      ? 'text-red-600 bg-red-50 border-red-200' 
      : 'text-yellow-600 bg-yellow-50 border-yellow-200';
  };
  
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-bold text-gray-800 flex items-center gap-1.5">
          <AlertTriangle className="w-4 h-4 text-red-600" />
          Zombie Sessions
        </h2>
        <button
          onClick={fetchData}
          disabled={loading}
          className="flex items-center gap-1.5 px-2 py-1.5 text-xs bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          Actualizar
        </button>
      </div>
      
      {/* Filtros Consolidados */}
      <div className="bg-white p-3 rounded-lg border border-gray-200">
        <div className="flex items-center gap-1.5 mb-2">
          <Filter className="w-3 h-3 text-gray-400" />
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Filtros</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Período</label>
            <select
              value={filters.rangeDays}
              onChange={(e) => setFilters({ ...filters, rangeDays: parseInt(e.target.value), page: 1 })}
              className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-blue-500"
            >
              <option value={7}>Últimos 7 días</option>
              <option value={30}>Últimos 30 días</option>
              <option value={90}>Últimos 90 días</option>
            </select>
          </div>
          
          <div>
            <label className="block text-xs text-gray-500 mb-1">Agente</label>
            <select
              value={filters.agentId}
              onChange={(e) => setFilters({ ...filters, agentId: e.target.value, page: 1 })}
              className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-blue-500"
            >
              <option value="all">Todos los agentes</option>
              {agents.map(agent => (
                <option key={agent.agentId} value={agent.agentId}>
                  {agent.agentId.substring(0, 30)}...
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-xs text-gray-500 mb-1">Pausadas hace más de</label>
            <select
              value={filters.minMinutes}
              onChange={(e) => setFilters({ ...filters, minMinutes: parseInt(e.target.value), page: 1 })}
              className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-blue-500"
            >
              <option value={1}>1 minuto</option>
              <option value={5}>5 minutos</option>
              <option value={10}>10 minutos</option>
              <option value={30}>30 minutos</option>
              <option value={60}>1 hora</option>
            </select>
          </div>
          
          <div>
            <label className="block text-xs text-gray-500 mb-1">Resultados por página</label>
            <select
              value={filters.limit}
              onChange={(e) => setFilters({ ...filters, limit: parseInt(e.target.value), page: 1 })}
              className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-blue-500"
            >
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      </div>
      
      {/* Error */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-xs text-red-800">
          <XCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}
      
      {/* Métricas clave */}
      {metrics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
          {/* Total Zombies */}
          <div className="bg-white p-2.5 md:p-4 rounded-lg border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs text-gray-500">Total Zombies</span>
              <AlertOctagon className="w-4 h-4 text-yellow-600" />
            </div>
            <div className="text-xl md:text-2xl font-bold text-gray-900">{metrics.totalZombies}</div>
            <div className="text-xs text-gray-500 mt-1">
              {metrics.zombieRate.toFixed(2)}% de {metrics.totalSessions} sesiones
            </div>
          </div>
          
          {/* Zombies Críticos */}
          <div className={`p-2.5 md:p-4 rounded-lg border shadow-sm ${
            metrics.criticalZombies > 0 ? 'bg-red-50 border-red-300' : 'bg-white border-gray-200'
          }`}>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs text-gray-500">Sesiones Críticas</span>
              <AlertTriangle className={`w-4 h-4 ${
                metrics.criticalZombies > 0 ? 'text-red-600' : 'text-gray-400'
              }`} />
            </div>
            <div className={`text-xl md:text-2xl font-bold ${
              metrics.criticalZombies > 0 ? 'text-red-600' : 'text-gray-900'
            }`}>
              {metrics.criticalZombies}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Pausadas &gt;5 min sin resolver
            </div>
          </div>
          
          {/* Eventos de Moderación */}
          <div className="bg-white p-2.5 md:p-4 rounded-lg border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex flex-col">
                <span className="text-xs text-gray-500">Moderaciones</span>
                <span className="text-xs text-gray-400">sin resolver</span>
              </div>
              <Activity className="w-4 h-4 text-blue-600" />
            </div>
            <div className="text-xl md:text-2xl font-bold text-gray-900">{metrics.unresolvedModerations}</div>
            <div className="text-xs text-gray-500 mt-1">
              Nota: Una sesión puede tener múltiples eventos
            </div>
          </div>
          
          {/* Freeze Events */}
          <div className="bg-white p-2.5 md:p-4 rounded-lg border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex flex-col">
                <span className="text-xs text-gray-500">Freeze Events</span>
                <span className="text-xs text-gray-400">Watchdog</span>
              </div>
              <Zap className="w-4 h-4 text-purple-600" />
            </div>
            <div className="text-xl md:text-2xl font-bold text-gray-900">{metrics.freezeEvents}</div>
            <div className="text-xs text-gray-500 mt-1">
              Pausas &gt;3s detectadas por sistema
            </div>
          </div>
        </div>
      )}
      
      {/* Nota explicativa */}
      {metrics && metrics.totalZombies > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <Activity className="w-3.5 h-3.5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-xs font-semibold text-blue-800 mb-1">Interpretación de métricas</h3>
              <p className="text-xs text-blue-800 leading-relaxed">
                <strong>Sesiones Zombie:</strong> Sesiones únicas pausadas sin resolver. 
                <strong className="ml-2">Eventos de Moderación/Freeze:</strong> Total de alertas del sistema (una sesión puede generar múltiples eventos). 
                El número de eventos puede ser mayor que el de sesiones.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Análisis de Problemas Críticos */}
      {(topAgents.length > 0 || oldestZombies.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          {/* Top Agentes Problemáticos */}
          <div className="bg-white p-3 rounded-lg border border-orange-200">
            <h3 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 text-orange-600" />
              Agentes con más Zombies
            </h3>
            <div className="space-y-2">
              {topAgents.slice(0, 3).map((agent, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-orange-50 rounded">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-gray-900 truncate">
                      {agent.agentId.split('-').slice(0, 3).join('-')}...
                    </div>
                  </div>
                  <div className="ml-2 flex items-center gap-2">
                    <span className="text-sm font-bold text-orange-600">{agent.count}</span>
                    <span className="text-xs text-gray-500">zombies</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Zombies Más Antiguos */}
          <div className="bg-white p-3 rounded-lg border border-red-200">
            <h3 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5 text-red-600" />
              Atención Urgente
            </h3>
            <div className="space-y-2">
              {oldestZombies.map((session, idx) => (
                <div key={idx} className="p-2 bg-red-50 rounded">
                  <div className="flex items-center justify-between mb-1">
                    <code className="text-xs bg-white px-1 rounded">{session.sessionId.substring(0, 12)}...</code>
                    <span className="text-xs font-bold text-red-600">
                      {formatDuration(session.pausedDuration)}
                    </span>
                  </div>
                  <div className="text-xs text-gray-600">
                    {session.userName} • {session.completedTurns}/{session.maxTurns} turnos
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Tendencia Últimos 7 Días */}
          <div className="bg-white p-3 rounded-lg border border-blue-200">
            <h3 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-1.5">
              <Activity className="w-3.5 h-3.5 text-blue-600" />
              Tendencia (7 días)
            </h3>
            <div className="space-y-1">
              {dailyTrend.map((day, idx) => {
                const maxCount = Math.max(...dailyTrend.map(d => d.count), 1);
                const widthPercent = (day.count / maxCount) * 100;
                const dateObj = new Date(day.date);
                const dayName = dateObj.toLocaleDateString('es-ES', { weekday: 'short' });
                
                return (
                  <div key={idx} className="flex items-center gap-2">
                    <span className="text-xs text-gray-600 w-8">{dayName}</span>
                    <div className="flex-1 bg-gray-100 rounded-full h-5 relative">
                      <div 
                        className="bg-blue-500 h-5 rounded-full transition-all duration-300 flex items-center justify-end pr-2"
                        style={{ width: `${Math.max(widthPercent, 5)}%` }}
                      >
                        {day.count > 0 && (
                          <span className="text-xs font-bold text-white">{day.count}</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      
      {/* Acciones bulk */}
      {selectedSessions.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="flex items-center gap-1.5">
            <CheckCircle className="w-4 h-4 text-blue-600" />
            <span className="text-xs font-semibold text-blue-900">
              {selectedSessions.length} sesión(es) seleccionadas
            </span>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={() => setSelectedSessions([])}
              className="w-full sm:w-auto px-3 py-1.5 text-xs bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition font-medium"
            >
              Cancelar
            </button>
            <button
              onClick={() => { setCleanupAction('close'); handleCleanup(); }}
              className="w-full sm:w-auto px-3 py-1.5 text-xs bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition font-medium flex items-center justify-center gap-1.5"
            >
              <XCircle className="w-3.5 h-3.5" />
              Cerrar
            </button>
            <button
              onClick={() => { setCleanupAction('delete'); handleCleanup(); }}
              className="w-full sm:w-auto px-3 py-1.5 text-xs bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-medium flex items-center justify-center gap-1.5"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Eliminar
            </button>
          </div>
        </div>
      )}
      
      {/* Tabla de sesiones */}
      {/* Vista móvil - tarjetas */}
      <div className="md:hidden space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-8 text-gray-500 text-sm">
            <RefreshCw className="w-5 h-5 animate-spin mr-2" />
            Cargando...
          </div>
        ) : sessions.length === 0 ? (
          <div className="text-center py-8 text-gray-500 text-sm">
            <CheckCircle className="w-6 h-6 mx-auto mb-2 text-green-500" />
            No hay sesiones zombie
          </div>
        ) : (
          sessions.map((session) => (
            <div key={session.sessionId} className={`bg-white rounded-lg border shadow-sm p-3 ${session.isCritical ? 'border-red-300' : 'border-yellow-300'}`}>
              <div className="flex items-center gap-2 mb-2">
                <input
                  type="checkbox"
                  checked={selectedSessions.includes(session.sessionId)}
                  onChange={() => handleSelectSession(session.sessionId)}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 flex-shrink-0"
                />
                {session.isCritical ? (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-100 px-1.5 py-0.5 rounded flex-shrink-0">
                    <AlertTriangle className="w-3 h-3" />CRÍTICO
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-yellow-700 bg-yellow-100 px-1.5 py-0.5 rounded flex-shrink-0">
                    <Clock className="w-3 h-3" />Zombie
                  </span>
                )}
                <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded truncate flex-1 min-w-0">
                  {session.sessionId.substring(0, 16)}…
                </code>
              </div>
              <div className="flex items-center justify-between text-xs mb-2">
                <span className="font-medium text-gray-900 truncate mr-2">{session.userName || 'N/A'}</span>
                <span className="text-gray-500 truncate">{session.agentId}</span>
              </div>
              <div className="flex items-center justify-between mb-2">
                <span className={`text-lg font-bold ${session.isCritical ? 'text-red-600' : 'text-yellow-600'}`}>
                  {formatDuration(session.pausedDuration)}
                </span>
                <span className="text-xs text-gray-500">{session.completedTurns}/{session.maxTurns} turnos</span>
              </div>
              <div className="flex flex-wrap gap-1 mb-3">
                {session.hasModeration && (
                  <span className="inline-flex items-center gap-1 text-xs text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded">
                    <Activity className="w-3 h-3" />Modal no renderizado
                  </span>
                )}
                {session.hasFreeze && (
                  <span className="inline-flex items-center gap-1 text-xs text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded">
                    <Zap className="w-3 h-3" />Watchdog
                  </span>
                )}
                {!session.hasModeration && !session.hasFreeze && (
                  <span className="inline-flex items-center gap-1 text-xs text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded">
                    <Clock className="w-3 h-3" />Sin evento
                  </span>
                )}
              </div>
              <button
                onClick={() => handleViewDetails(session.sessionId)}
                className="w-full flex items-center justify-center gap-1.5 text-sm text-white bg-blue-600 hover:bg-blue-700 font-medium py-1.5 rounded-lg transition"
              >
                <Eye className="w-4 h-4" />
                Ver detalles
              </button>
            </div>
          ))
        )}
      </div>
      {/* Vista desktop - tabla */}
      <div className="hidden md:block bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedSessions.length === sessions.length && sessions.length > 0}
                    onChange={handleSelectAll}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                  Estado
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                  Session ID
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                  Usuario
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                  Agente
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                  Fecha
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                  Turnos
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                  Tiempo Pausada
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                  Moderaciones
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                  Diagnóstico
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={10} className="px-4 py-8 text-center text-gray-500">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                    Cargando...
                  </td>
                </tr>
              ) : sessions.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-8 text-center text-gray-500">
                    <CheckCircle className="w-6 h-6 mx-auto mb-2 text-green-500" />
                    No hay sesiones zombie con los filtros seleccionados
                  </td>
                </tr>
              ) : (
                sessions.map((session) => (
                  <tr key={session.sessionId} className="hover:bg-gray-50 transition">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedSessions.includes(session.sessionId)}
                        onChange={() => handleSelectSession(session.sessionId)}
                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {session.isCritical ? (
                          <div className="flex items-center gap-1 text-red-600">
                            <AlertTriangle className="w-4 h-4" />
                            <span className="text-xs font-medium">CRÍTICO</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 text-yellow-600">
                            <Clock className="w-4 h-4" />
                            <span className="text-xs font-medium">Zombie</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                        {session.sessionId.substring(0, 20)}...
                      </code>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {session.userName || 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {session.agentId}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {session.createdAt ? formatDate(session.createdAt) : 
                       session.updatedAt ? formatDate(session.updatedAt) : 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {session.completedTurns}/{session.maxTurns}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-sm font-medium ${
                        session.isCritical ? 'text-red-600' : 'text-yellow-600'
                      }`}>
                        {formatDuration(session.pausedDuration)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {session.moderationCount > 0 ? (
                        <span className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-700 rounded-full font-bold text-sm">
                          {session.moderationCount}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-sm">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1">
                        {session.hasModeration && (
                          <span className="inline-flex items-center gap-1 text-xs text-blue-700 bg-blue-100 px-2 py-0.5 rounded">
                            <Activity className="w-3 h-3" />
                            Modal no renderizado
                          </span>
                        )}
                        {session.hasFreeze && (
                          <span className="inline-flex items-center gap-1 text-xs text-purple-700 bg-purple-100 px-2 py-0.5 rounded">
                            <Zap className="w-3 h-3" />
                            Watchdog activo
                          </span>
                        )}
                        {!session.hasModeration && !session.hasFreeze && (
                          <span className="inline-flex items-center gap-1 text-xs text-gray-600 bg-gray-100 px-2 py-0.5 rounded">
                            <Clock className="w-3 h-3" />
                            Pausada sin evento
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleViewDetails(session.sessionId)}
                        className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium"
                      >
                        <Eye className="w-4 h-4" />
                        Ver
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Modal de Detalles */}
      {showDetailsModal && sessionDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">Detalles de Sesión Zombie</h3>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Info básica */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Información Básica</h4>
                  <div className="bg-gray-50 p-4 rounded-lg space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Session ID:</span>
                      <code className="bg-gray-200 px-2 py-0.5 rounded text-xs">
                        {sessionDetails.session.sessionId.substring(0, 16)}...
                      </code>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Usuario:</span>
                      <span className="font-medium">{sessionDetails.session.userName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Turnos:</span>
                      <span className="font-medium">
                        {sessionDetails.session.completedTurns}/{sessionDetails.session.maxTurns}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Creada:</span>
                      <span>{formatDate(sessionDetails.session.createdAt)}</span>
                    </div>
                  </div>
                </div>
                
                {/* Diagnóstico Rápido */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Diagnóstico</h4>
                  <div className="bg-red-50 border border-red-200 p-4 rounded-lg space-y-2">
                    <div className="flex items-center gap-2 text-red-700">
                      <AlertTriangle className="w-4 h-4" />
                      <span className="font-bold text-sm">PROBLEMA DETECTADO</span>
                    </div>
                    <div className="text-sm text-gray-700">
                      {sessionDetails.moderationEvents.length > 0 && (
                        <div className="flex items-start gap-2 mt-2">
                          <Activity className="w-4 h-4 text-blue-600 mt-0.5" />
                          <div>
                            <div className="font-medium">Modal no renderizado</div>
                            <div className="text-xs text-gray-600">
                              {sessionDetails.moderationEvents.length} evento(s) de moderación sin resolver
                            </div>
                          </div>
                        </div>
                      )}
                      {sessionDetails.telemetryEvents.length > 0 && (
                        <div className="flex items-start gap-2 mt-2">
                          <Zap className="w-4 h-4 text-purple-600 mt-0.5" />
                          <div>
                            <div className="font-medium">Freeze detectado</div>
                            <div className="text-xs text-gray-600">
                              Watchdog registró {sessionDetails.telemetryEvents.length} congelamiento(s)
                            </div>
                          </div>
                        </div>
                      )}
                      {sessionDetails.moderationEvents.length === 0 && sessionDetails.telemetryEvents.length === 0 && (
                        <div className="flex items-start gap-2 mt-2">
                          <Clock className="w-4 h-4 text-gray-600 mt-0.5" />
                          <div>
                            <div className="font-medium">Pausada sin evento claro</div>
                            <div className="text-xs text-gray-600">
                              No hay logs de moderación ni freeze
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Timeline de Eventos */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <Activity className="w-4 h-4" />
                  Timeline Completa de Eventos
                </h4>
                <div className="relative border-l-2 border-gray-300 ml-3 space-y-4">
                  {/* Combinar y ordenar todos los eventos */}
                  {[
                    ...sessionDetails.moderationEvents.map(e => ({ ...e, type: 'moderation' })),
                    ...sessionDetails.systemEvents.map(e => ({ ...e, type: 'system' })),
                    ...sessionDetails.telemetryEvents.map(e => ({ ...e, type: 'telemetry' })),
                    ...sessionDetails.session.messages.map(e => ({ ...e, type: 'message' }))
                  ]
                  .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
                  .map((event, idx) => {
                    let icon, bgColor, borderColor, textColor, title, content;
                    
                    if (event.type === 'moderation') {
                      icon = <ShieldAlert className="w-4 h-4" />;
                      bgColor = 'bg-orange-50';
                      borderColor = 'border-orange-300';
                      textColor = 'text-orange-700';
                      title = `⚠️ MODERACIÓN - ${event.moderation?.severity?.toUpperCase() || 'ALERT'}`;
                      content = (
                        <>
                          <div className="font-medium">{event.moderation?.reason || 'Contenido moderado'}</div>
                          {event.moderation?.inputEvaluated && (
                            <div className="text-xs mt-1 opacity-75">
                              Input: "{event.moderation.inputEvaluated.substring(0, 80)}..."
                            </div>
                          )}
                        </>
                      );
                    } else if (event.type === 'system') {
                      icon = <CheckCircle className="w-4 h-4" />;
                      bgColor = 'bg-green-50';
                      borderColor = 'border-green-300';
                      textColor = 'text-green-700';
                      title = event.metadata?.systemEvent || event.eventType;
                      content = <div className="text-xs">{event.metadata?.phaseLabel}</div>;
                    } else if (event.type === 'telemetry') {
                      icon = <Zap className="w-4 h-4" />;
                      bgColor = 'bg-purple-50';
                      borderColor = 'border-purple-300';
                      textColor = 'text-purple-700';
                      title = `FREEZE - ${event.freezeDurationMs}ms`;
                      content = <div className="text-xs">Correlation: {event.correlationId}</div>;
                    } else {
                      icon = event.role === 'user' ? '👤' : '🤖';
                      bgColor = event.role === 'user' ? 'bg-blue-50' : 'bg-gray-50';
                      borderColor = event.role === 'user' ? 'border-blue-200' : 'border-gray-200';
                      textColor = 'text-gray-700';
                      title = `MENSAJE - ${event.role.toUpperCase()}`;
                      content = (
                        <div className="text-xs">
                          {event.content?.substring(0, 100)}
                          {event.content?.length > 100 && '...'}
                        </div>
                      );
                    }
                    
                    return (
                      <div key={idx} className="relative pl-6 pb-4">
                        <div className={`absolute left-[-1.125rem] top-0 w-6 h-6 rounded-full border-2 ${borderColor} ${bgColor} flex items-center justify-center`}>
                          {typeof icon === 'string' ? icon : icon}
                        </div>
                        <div className={`${bgColor} border ${borderColor} p-3 rounded-lg`}>
                          <div className="flex items-center justify-between mb-1">
                            <span className={`text-xs font-bold ${textColor}`}>{title}</span>
                            <span className="text-xs text-gray-500">
                              {formatDate(event.timestamp)}
                            </span>
                          </div>
                          <div className="text-sm text-gray-700">{content}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {/* Análisis de Moderación (si hay) */}
              {sessionDetails.moderationEvents.length > 0 && sessionDetails.moderationEvents[0].moderation?.categories && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Análisis de Moderación</h4>
                  <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                    <div className="grid grid-cols-2 gap-4">
                      {Object.entries(sessionDetails.moderationEvents[0].moderation.scores || {}).map(([category, score]) => (
                        <div key={category} className="space-y-1">
                          <div className="flex items-center justify-between text-sm">
                            <span className="font-medium text-gray-700 capitalize">{category}</span>
                            <span className={`font-bold ${score > 0.7 ? 'text-red-600' : score > 0.4 ? 'text-yellow-600' : 'text-green-600'}`}>
                              {(score * 100).toFixed(0)}%
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${score > 0.7 ? 'bg-red-500' : score > 0.4 ? 'bg-yellow-500' : 'bg-green-500'}`}
                              style={{ width: `${score * 100}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Modal de Confirmación de Limpieza */}
      {showCleanupModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <AlertTriangle className="w-6 h-6 text-yellow-600" />
                <h3 className="text-lg font-bold text-gray-900">
                  Confirmar Limpieza
                </h3>
              </div>
              
              <p className="text-gray-700 mb-4">
                Vas a procesar <strong>{selectedSessions.length}</strong> sesión(es) zombie.
              </p>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Acción a realizar:
                </label>
                <select
                  value={cleanupAction}
                  onChange={(e) => setCleanupAction(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="close">Cerrar (marcar como closed)</option>
                  <option value="delete">Eliminar permanentemente</option>
                </select>
                
                <p className="text-xs text-gray-500 mt-2">
                  {cleanupAction === 'close' 
                    ? '✅ Recomendado: Cierra limpiamente sin eliminar datos'
                    : '⚠️ Cuidado: Elimina permanentemente sesiones y logs'}
                </p>
              </div>
              
              <div className="flex gap-2">
                <button
                  onClick={() => setShowCleanupModal(false)}
                  className="flex-1 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmCleanup}
                  className={`flex-1 px-4 py-2 text-white rounded-lg transition ${
                    cleanupAction === 'delete'
                      ? 'bg-red-600 hover:bg-red-700'
                      : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  Confirmar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ZombieSessionsPanel;
